import { z } from 'zod';
import { VISUALS_MODELS } from '../types/visuals-generator.js';
const ModelIdEnum = z.enum(VISUALS_MODELS.map((m) => m.id));
export const CreateVisualGenerationRequestSchema = z.object({
    modelId: ModelIdEnum,
    /**
     * Optional at the wire level ONLY because reframe/outpaint models take no
     * prompt (`fal.promptless`). Every other model still requires a non-empty
     * one — enforced per-model in visuals-generator.service.ts, which knows
     * which model was chosen. Do not read this as "prompt is optional".
     */
    prompt: z.string().trim().max(8_000).optional(),
    aspectRatio: z.string().min(1).max(20).optional(),
    inputAssetUrl: z.string().url().max(2_000).optional(),
    extraParams: z.record(z.string(), z.unknown()).optional(),
});
/**
 * Share — package selected generations into individual mail attachments and
 * route via internal mail. Capped at 5 by MAIL_LIMITS.maxAttachments; the
 * route returns 400 if any single asset exceeds the per-attachment cap.
 */
export const ShareVisualsRequestSchema = z.object({
    generationIds: z.array(z.string().uuid()).min(1).max(5),
    recipientUserIds: z.array(z.string().uuid()).min(1).max(50),
});
export const SetKieAiKeyRequestSchema = z.object({
    apiKey: z.string().trim().min(8).max(500),
});
export const SetFalAiKeyRequestSchema = z.object({
    apiKey: z.string().trim().min(8).max(500),
});
export const PatchVisualsGeneratorConfigRequestSchema = z
    .object({
    driveFolderInput: z.string().max(500).nullable().optional(),
    /**
     * Loose validation on the wire: any non-empty string is accepted. The
     * service-side filter drops ids the catalog no longer knows about so a
     * catalog rename never bricks an admin's existing selection.
     */
    enabledModelIds: z.array(z.string().min(1).max(200)).max(200).optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
export const GrantVisualsGeneratorBodySchema = z.object({
    userId: z.string().uuid(),
});
export const VisualsGenerationIdParamSchema = z.object({
    id: z.string().uuid(),
});
// ----------------------------------------------------------------------------
// Prompt-mixer AI: templates + config + generate
// ----------------------------------------------------------------------------
export const CreateVisualsPromptTemplateRequestSchema = z.object({
    name: z.string().trim().min(1).max(100),
    body: z.string().trim().min(1).max(20_000),
});
export const UpdateVisualsPromptTemplateRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(100).optional(),
    body: z.string().trim().min(1).max(20_000).optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
// v2.0.0 PR #C — visuals_prompt_ai_config table dropped (Q7 lock). PATCH
// route was removed alongside; the schema is kept for one release as a
// no-op redirect so any stale frontend bundles fail loudly with a clear
// message rather than silently appearing to save.
export const PatchVisualsPromptAiConfigRequestSchema = z
    .object({})
    .refine(() => false, {
    message: 'The Prompt Mixer is now configured via Settings → Agent Profiles → Visuals Prompt Mixer.',
});
export const GeneratePromptRequestSchema = z
    .object({
    idea: z.string().trim().max(10_000).optional(),
    inputPrompt: z.string().trim().max(10_000).optional(),
    templatePrompt: z.string().trim().max(20_000).optional(),
    targetModelId: z.string().max(200).optional(),
})
    .refine((d) => {
    const filled = [d.idea, d.inputPrompt, d.templatePrompt].filter((s) => typeof s === 'string' && s.trim().length > 0).length;
    return filled >= 2;
}, {
    message: 'Fill at least two of: Idea, Input prompt, Template prompt',
});
//# sourceMappingURL=visuals-generator.schema.js.map